import React, { useMemo, useRef, useState } from 'react';
import { AlertTriangleIcon, FileTextIcon, UploadIcon, XIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, Input, Select, Textarea } from '../ui/Field';
import { useNetwork } from '../../contexts/NetworkContext';
import { CAMPUS_INTAKE } from '../../types/network';
import type { ApplicantStatus, ApplicationDocument } from '../../types/network';
const STEPS = ['About you', 'Your campus', 'Your location', 'Availability', 'Capabilities', 'Motivation', 'Documents', 'Consent'] as const;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PERIODS = ['Morning', 'Afternoon', 'Evening'];
const TRANSPORT = ['Walking', 'Bicycle', 'Motorcycle', 'Public transport', 'Personal vehicle', 'Other'];

/**
 * Areas of interest — deliberately not "what you will do". Which activities a
 * representative is actually authorised to perform is decided later by rank,
 * training, campus need and safety.
 */
const INTERESTS = ['Property verification', 'Property acquisition and landlord onboarding', 'Business acquisition', 'Campus intelligence', 'Student referrals', 'Campus events'];
const CONSENTS = [{
  key: 'privacy',
  label: 'I have read the privacy notice. Campuzon stores my documents securely, uses them only to assess this application and confirm my identity, and does not publish my phone number or address.'
}, {
  key: 'contact',
  label: 'Campuzon may contact me by email and phone about this application.'
}, {
  key: 'terms',
  label: 'I understand representatives are authorised Campuzon field operators, hold no institutional authority, and that earnings exist only for work a reviewer approves.'
}];
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'application/pdf'];
function emptyDocuments(): ApplicationDocument[] {
  return [{
    key: 'student-id',
    label: 'Student ID or admission letter',
    hint: 'JPG, PNG or PDF, up to 5 MB. The whole card, with the expiry visible.',
    required: true,
    fileName: null,
    sizeBytes: null,
    mimeType: null,
    previewUrl: null,
    state: 'Not provided',
    reviewNote: null
  }, {
    key: 'photo',
    label: 'Profile photograph',
    hint: 'A clear photograph of your face. JPG or PNG, up to 5 MB.',
    required: true,
    fileName: null,
    sizeBytes: null,
    mimeType: null,
    previewUrl: null,
    state: 'Not provided',
    reviewNote: null
  }];
}
function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
export function ApplicationWizard({
  open,
  onOpen,
  onSubmitted
}: {
  open: boolean;
  onOpen: () => void;
  onSubmitted: (result: {
    id: string;
    held: boolean;
  }) => void;
}) {
  const network = useNetwork();

  /** Campuses come from configuration, never a hard-coded list in this form. */
  const openCampuses = useMemo(() => network.campuses.filter(campus => CAMPUS_INTAKE[campus.recruitmentStatus].open), [network.campuses]);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    campusId: openCampuses[0]?.id ?? '',
    applicantStatus: 'Current student' as ApplicantStatus,
    programme: '',
    level: '200 level',
    graduationYear: '2028',
    area: '',
    city: '',
    state: '',
    transport: 'Walking',
    weeklyHours: '5 – 10 hours',
    connectivity: 'Mobile data, mostly reliable',
    hasSmartphone: false,
    hasCamera: false,
    experience: '',
    motivation: ''
  });
  const [days, setDays] = useState<string[]>([]);
  const [periods, setPeriods] = useState<string[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [documents, setDocuments] = useState<ApplicationDocument[]>(emptyDocuments);
  const [consents, setConsents] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const campus = network.campuses.find(item => item.id === form.campusId) ?? null;
  const intake = campus ? CAMPUS_INTAKE[campus.recruitmentStatus] : null;
  if (!open) {
    return <Panel title="Ready to apply?" data-id="element-3261" data-mp="wqodd3dv50ef">
        <p className="text-[13px] leading-6 text-ink-muted" data-id="element-3262" data-mp="wnk1dju5p0d7">
          Eight short steps, about five minutes. We ask only what operations needs to place you on a
          campus — no emergency contact and no home address at this stage. Those come later, during
          onboarding, and only if you are selected.
        </p>
        <Button variant="primary" className="mt-3" onClick={onOpen} data-id="element-3263" data-mp="dz5acwju9mce">
          Start my application
        </Button>
      </Panel>;
  }

  /* --------------------------------------------------------- validation -- */

  function problemsFor(index: number): string[] {
    const problems: string[] = [];
    if (index === 0) {
      if (form.fullName.trim().length < 3) problems.push('your full name');
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) problems.push('a valid email address');
      if (form.phone.trim().length < 8) problems.push('a phone number we can reach you on');
    }
    if (index === 1) {
      if (!form.campusId) problems.push('a campus');
      if (form.programme.trim().length < 3) problems.push('your course or programme of study');
      if (!/^(19|20)\d{2}$/.test(form.graduationYear.trim())) problems.push('a graduation year');
    }
    if (index === 2) {
      if (form.area.trim().length < 2) problems.push('the area you live in');
      if (form.city.trim().length < 2) problems.push('your city');
      if (form.state.trim().length < 2) problems.push('your state');
    }
    if (index === 3 && days.length === 0) problems.push('at least one day you are usually available');
    if (index === 4) {
      if (!form.hasSmartphone) problems.push('a smartphone — the field work is not possible without one');
      if (!form.hasCamera) problems.push('a working camera, since evidence is photographic');
    }
    if (index === 5 && form.motivation.trim().length < 24) {
      problems.push('a few sentences on why you want to do this');
    }
    if (index === 6) {
      documents.filter(document => document.required && document.state === 'Not provided').forEach(document => problems.push(document.label.toLowerCase()));
    }
    if (index === 7) {
      CONSENTS.filter(consent => !consents[consent.key]).forEach(() => problems.push('each acknowledgement'));
    }
    return problems;
  }
  function advance() {
    const problems = problemsFor(step);
    if (problems.length > 0) {
      setError(`Still needed: ${Array.from(new Set(problems)).join(', ')}.`);
      return;
    }
    setError(null);
    setStep(current => Math.min(current + 1, STEPS.length - 1));
  }
  function submit() {
    const problems = STEPS.flatMap((_, index) => problemsFor(index));
    if (problems.length > 0) {
      setError(`Still needed: ${Array.from(new Set(problems)).join(', ')}.`);
      return;
    }
    if (!campus) return;
    const applicationId = network.submitApplication({
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      institution: campus.institution,
      campusId: campus.id,
      campusStatusAtApplication: campus.recruitmentStatus,
      applicantStatus: form.applicantStatus,
      programme: form.programme.trim(),
      level: form.level,
      graduationYear: form.graduationYear.trim(),
      location: {
        area: form.area.trim(),
        city: form.city.trim(),
        state: form.state.trim()
      },
      residentialArea: form.area.trim(),
      weeklyHours: form.weeklyHours,
      availableDays: days,
      fieldPeriods: periods,
      transport: form.transport,
      hasSmartphone: form.hasSmartphone,
      hasCamera: form.hasCamera,
      connectivity: form.connectivity,
      experience: form.experience.trim(),
      motivation: form.motivation.trim(),
      preferredActivities: interests,
      consents: CONSENTS.map(consent => ({
        key: consent.key,
        label: consent.label,
        given: Boolean(consents[consent.key])
      })),
      documents: documents.map(document => document.state === 'Not provided' ? document : {
        ...document,
        state: 'Submitted' as const
      })
    });
    const held = Boolean(intake?.held);
    if (held) {
      network.moveApplication(applicationId, 'Waitlisted', `${campus.institution} is at ${campus.recruitmentStatus}. Application held until intake opens.`);
    }
    onSubmitted({
      id: applicationId,
      held
    });
  }

  /* --------------------------------------------------------------- view -- */

  return <Panel title="Representative application" description={`Step ${step + 1} of ${STEPS.length} — ${STEPS[step]}`} data-id="element-3264" data-mp="1kiontq2ni0st">
      <ol className="mb-5 flex flex-wrap gap-1.5" aria-label="Application steps" data-id="element-3265" data-mp="1afjbcvepfpfl">
        {STEPS.map((label, index) => <li key={label} data-id="element-3266" data-mp="10nveug3aji79" data-mpi={index}>
            <button type="button" onClick={() => {
          setError(null);
          setStep(index);
        }} aria-current={index === step ? 'step' : undefined} className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors duration-150 ease-out ${index === step ? 'border-brand-700 bg-brand-50 text-brand-800' : problemsFor(index).length === 0 ? 'border-ok-100 bg-ok-50 text-ok-700' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-3267" data-mp="10oeufq5gxui4">
              {index + 1}. {label}
            </button>
          </li>)}
      </ol>

      {step === 0 && <div className="grid gap-3 sm:grid-cols-2" data-id="element-3268" data-mp="230j2toq3047b">
          <Field label="Full name" data-id="element-3269" data-mp="22ypvs8bk3hys">
            <Input value={form.fullName} onChange={event => setForm({
          ...form,
          fullName: event.target.value
        })} data-id="element-3270" data-mp="27qzf35i61js3" />
          </Field>
          <Field label="Email" data-id="element-3271" data-mp="1u1fi6gb9ngqp">
            <Input type="email" value={form.email} onChange={event => setForm({
          ...form,
          email: event.target.value
        })} data-id="element-3272" data-mp="1y0wplfa96nyi" />
          </Field>
          <Field label="Phone" hint="Never published. Operations use it to reach you about this application." data-id="element-3273" data-mp="2qpv7cbagro3a">
            <Input value={form.phone} onChange={event => setForm({
          ...form,
          phone: event.target.value
        })} data-id="element-3274" data-mp="2cqcawfr0m16t" />
          </Field>
        </div>}

      {step === 1 && <div className="space-y-3" data-id="element-3275" data-mp="11anpvtxbnc8n">
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-3276" data-mp="119m2cpz4cxa1">
            <Field label="Preferred campus" data-id="element-3277" data-mp="16ajlb4rydvcl">
              <Select value={form.campusId} onChange={event => setForm({
            ...form,
            campusId: event.target.value
          })} data-id="element-3278" data-mp="16bbfcb5wjl0r">
                {openCampuses.map(item => <option key={item.id} value={item.id} data-id="element-3279" data-mp="tth7oo1pfcpw">
                    {item.institution} — {item.recruitmentStatus}
                  </option>)}
              </Select>
            </Field>
            <Field label="Your status" data-id="element-3280" data-mp="12q9jz50h69j7">
              <Select value={form.applicantStatus} onChange={event => setForm({
            ...form,
            applicantStatus: event.target.value as ApplicantStatus
          })} data-id="element-3281" data-mp="f3jnej6wb75z">
                <option data-id="element-3282" data-mp="otmfmh670h39">Current student</option>
                <option data-id="element-3283" data-mp="k12yxbc7l5u8">Recent graduate</option>
                <option data-id="element-3284" data-mp="k0a14zv89qo8">Other</option>
              </Select>
            </Field>
            <Field label="Course / programme of study" data-id="element-3285" data-mp="3sg3iti1ap86v">
              <Input value={form.programme} onChange={event => setForm({
            ...form,
            programme: event.target.value
          })} data-id="element-3286" data-mp="3nil75jvw8ev6" />
            </Field>
            <Field label="Level or year" data-id="element-3287" data-mp="1rdcczpmmf5mg">
              <Select value={form.level} onChange={event => setForm({
            ...form,
            level: event.target.value
          })} data-id="element-3288" data-mp="1wd87my9fhkpc">
                {['100 level', '200 level', '300 level', '400 level', '500 level', 'Postgraduate', 'Graduated'].map(item => <option key={item} data-id="element-3289" data-mp="18gayne76ft5u">{item}</option>)}
              </Select>
            </Field>
            <Field label={form.applicantStatus === 'Recent graduate' ? 'Graduation year' : 'Expected graduation year'} hint="Used to move you into the alumni network at the right time." data-id="element-3290" data-mp="1dem99r5aq7p4">
              <Input value={form.graduationYear} onChange={event => setForm({
            ...form,
            graduationYear: event.target.value
          })} data-id="element-3291" data-mp="1mhn9jb7hd4kv" />
            </Field>
          </div>
          {campus && intake && <p className={`rounded-lg border px-3.5 py-2.5 text-[12px] leading-5 ${intake.held ? 'border-warn-100 bg-warn-50 text-warn-700' : 'border-line bg-canvas text-ink-muted'}`} data-id="element-3292" data-mp="37ywet8qa2y1k">
              <span className="font-semibold" data-id="element-3293" data-mp="2y705t2mnlnik">{campus.institution}</span> — {campus.recruitmentStatus}.{' '}
              {intake.note}
            </p>}
        </div>}

      {step === 2 && <div className="space-y-3" data-id="element-3294" data-mp="2k9ld9p13szmd">
          <div className="grid gap-3 sm:grid-cols-3" data-id="element-3295" data-mp="2k8jz3bosjx0f">
            <Field label="Area" hint="Akoka, Bariga, Ijoka…" data-id="element-3296" data-mp="2kbxziedw9w58">
              <Input value={form.area} onChange={event => setForm({
            ...form,
            area: event.target.value
          })} data-id="element-3297" data-mp="2fb0l8cw09m90" />
            </Field>
            <Field label="City" data-id="element-3298" data-mp="2bb9n1o45g3ug">
              <Input value={form.city} onChange={event => setForm({
            ...form,
            city: event.target.value
          })} data-id="element-3299" data-mp="djnfxmq4lccf" />
            </Field>
            <Field label="State" data-id="element-3300" data-mp="9iv5wr9fj3f3">
              <Input value={form.state} onChange={event => setForm({
            ...form,
            state: event.target.value
          })} data-id="element-3301" data-mp="9fpkpn3x8lon" />
            </Field>
          </div>
          <Field label="How do you normally move around your campus area?" data-id="element-3302" data-mp="lis6c8vyepbi">
            <Select value={form.transport} onChange={event => setForm({
          ...form,
          transport: event.target.value
        })} data-id="element-3303" data-mp="7kbxocej6oqq">
              {TRANSPORT.map(item => <option key={item} data-id="element-3304" data-mp="3tz4vfdu1pctx">{item}</option>)}
            </Select>
          </Field>
          <p className="rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-3305" data-mp="2lha6x5mcc0a">
            Area, city and state are enough to place you in a campus zone. We do not ask for your street
            address, and we never publish where you live.
          </p>
        </div>}

      {step === 3 && <div className="space-y-4" data-id="element-3306" data-mp="3b1am0yp08zb0">
          <Field label="Hours a week you can commit" data-id="element-3307" data-mp="3fzvo73ntht23">
            <Select value={form.weeklyHours} onChange={event => setForm({
          ...form,
          weeklyHours: event.target.value
        })} data-id="element-3308" data-mp="3fzc87shq9eqp">
              {['Under 5 hours', '5 – 10 hours', '10 – 15 hours', 'More than 15 hours'].map(item => <option key={item} data-id="element-3309" data-mp="35f0egf282zh3">{item}</option>)}
            </Select>
          </Field>
          <fieldset data-id="element-3310" data-mp="31cwype8it8q0">
            <legend className="text-[13px] font-medium text-ink" data-id="element-3311" data-mp="31bllknt6qom8">Which days are you generally available?</legend>
            <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-3312" data-mp="3fb3gzm0zl04k">
              {DAYS.map(day => {
            const on = days.includes(day);
            return <button key={day} type="button" aria-pressed={on} onClick={() => setDays(current => on ? current.filter(item => item !== day) : [...current, day])} className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors duration-150 ease-out ${on ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-3313" data-mp="3p1pqcyi40uet">
                    {day}
                  </button>;
          })}
            </div>
          </fieldset>
          <fieldset data-id="element-3314" data-mp="39gmo15thvwy1">
            <legend className="text-[13px] font-medium text-ink" data-id="element-3315" data-mp="39h67iqysmte6">
              Preferred field-work periods <span className="font-normal text-ink-subtle" data-id="element-3316" data-mp="3eeo6boj3js8g">(optional)</span>
            </legend>
            <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-3317" data-mp="3jg3v9roeaiw4">
              {PERIODS.map(period => {
            const on = periods.includes(period);
            return <button key={period} type="button" aria-pressed={on} onClick={() => setPeriods(current => on ? current.filter(item => item !== period) : [...current, period])} className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors duration-150 ease-out ${on ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:text-ink'}`} data-id="element-3318" data-mp="1p2w54dbd6vgt">
                    {period}
                  </button>;
          })}
            </div>
          </fieldset>
          <Field label="Your connectivity" data-id="element-3319" data-mp="1vg014uu4b7pv">
            <Select value={form.connectivity} onChange={event => setForm({
          ...form,
          connectivity: event.target.value
        })} data-id="element-3320" data-mp="1qj0419f8xfaw">
              {['Mobile data, mostly reliable', 'Mobile data, patchy where I live', 'Wi-Fi at home, mobile data outside', 'Limited — I would need to upload later'].map(item => <option key={item} data-id="element-3321" data-mp="2j8h31djypgo0">{item}</option>)}
            </Select>
          </Field>
        </div>}

      {step === 4 && <div className="space-y-3" data-id="element-3322" data-mp="259zmq0wdz6g4">
          {[{
        key: 'hasSmartphone' as const,
        label: 'I have a smartphone I can use for field work'
      }, {
        key: 'hasCamera' as const,
        label: 'Its camera works well enough for property photographs'
      }].map(item => <label key={item.key} className="flex items-start gap-2.5" data-id="element-3323" data-mp="32xhqsyf3rkds">
              <input type="checkbox" checked={form[item.key]} onChange={event => setForm({
          ...form,
          [item.key]: event.target.checked
        })} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-3324" data-mp="2o5zhtkknuq3x" />
              <span className="text-[13px] leading-6 text-ink" data-id="element-3325" data-mp="3lm69ddq45dme">{item.label}</span>
            </label>)}
          <fieldset data-id="element-3326" data-mp="1a5w3w0syhexn">
            <legend className="text-[13px] font-medium text-ink" data-id="element-3327" data-mp="1a532zggohjnf">
              Which areas are you most interested in supporting?
            </legend>
            <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-3328" data-mp="1izr5iyuvcn8j">
              An interest, not an assignment. What you are authorised to do depends on your rank,
              completed training, campus need and safety.
            </p>
            <div className="mt-2 space-y-1.5" data-id="element-3329" data-mp="r8lb8z1txjr6">
              {INTERESTS.map(activity => <label key={activity} className="flex items-start gap-2.5" data-id="element-3330" data-mp="majlxwdt8o79">
                  <input type="checkbox" checked={interests.includes(activity)} onChange={event => setInterests(current => event.target.checked ? [...current, activity] : current.filter(item => item !== activity))} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-3331" data-mp="109sz0n6pz270" />
                  <span className="text-[13px] leading-6 text-ink" data-id="element-3332" data-mp="1rhwdy8em90yh">{activity}</span>
                </label>)}
            </div>
          </fieldset>
        </div>}

      {step === 5 && <div className="space-y-3" data-id="element-3333" data-mp="21cy5fawd6f48">
          <Field label="Why do you want to do this?" data-id="element-3334" data-mp="2feahvccbfyld">
            <Textarea rows={4} value={form.motivation} onChange={event => setForm({
          ...form,
          motivation: event.target.value
        })} data-id="element-3335" data-mp="2fcpmxlm3admz" />
          </Field>
          <Field label="Relevant experience" hint="Optional. Course rep, hostel hunting, sales, photography — anything." data-id="element-3336" data-mp="2p3kd312ghxdp">
            <Textarea rows={3} value={form.experience} onChange={event => setForm({
          ...form,
          experience: event.target.value
        })} data-id="element-3337" data-mp="2k7d2guz47xpf" />
          </Field>
        </div>}

      {step === 6 && <div className="space-y-3" data-id="element-3338" data-mp="3csjvxc9v8jm6">
          {documents.map((document, index) => <DocumentUpload key={document.key} document={document} onChange={next => setDocuments(current => current.map((item, i) => i === index ? next : item))} data-id="element-3339" data-mp="3hr3gb88t9ncf" data-mpi={index} />)}
          <p className="rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-3340" data-mp="38rq96m85tauv">
            Documents are reviewed by operations: submitted, under review, then verified — or returned for
            re-upload with the reason. This is an authorised field role, so identity is confirmed before
            activation rather than taken on trust.
          </p>
        </div>}

      {step === 7 && <div className="space-y-3" data-id="element-3341" data-mp="2b1vc8gi5d25d">
          {CONSENTS.map(consent => <label key={consent.key} className="flex items-start gap-2.5" data-id="element-3342" data-mp="2g0yfbvekgyap">
              <input type="checkbox" checked={Boolean(consents[consent.key])} onChange={event => setConsents(current => ({
          ...current,
          [consent.key]: event.target.checked
        }))} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-3343" data-mp="2g32fq3d9bf68" />
              <span className="text-[12px] leading-5 text-ink-muted" data-id="element-3344" data-mp="7unwuehp29it">{consent.label}</span>
            </label>)}
          {intake?.held && <p className="flex items-start gap-2 rounded-lg border border-warn-100 bg-warn-50 px-3.5 py-2.5 text-[12px] leading-5 text-warn-700" data-id="element-3345" data-mp="14hj35h3xwmdd">
              <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-3346" data-mp="vi4fepf5qpwk" />
              This campus is not open yet, so your application will be waitlisted rather than reviewed
              immediately. You will hear from us when intake opens.
            </p>}
        </div>}

      {error && <p role="alert" className="mt-4 flex items-start gap-2 rounded-lg border border-danger-100 bg-danger-50 px-3.5 py-2.5 text-[12px] leading-5 text-danger-700" data-id="element-3347" data-mp="3b9jsg8zmscuo">
          <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-3348" data-mp="3669cbxe8bj9w" />
          {error}
        </p>}

      <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-line pt-4" data-id="element-3349" data-mp="3b2zsekq1aevb">
        {step > 0 && <Button variant="ghost" onClick={() => {
        setError(null);
        setStep(current => current - 1);
      }} data-id="element-3350" data-mp="3fxdpqso9aw2m">
            Back
          </Button>}
        {step < STEPS.length - 1 ? <Button variant="primary" onClick={advance} data-id="element-3351" data-mp="3tw3p6eyjt7u5">
            Continue
          </Button> : <Button variant="primary" onClick={submit} data-id="element-3352" data-mp="2ng31wshhglj">
            Submit application
          </Button>}
        <span className="ml-auto text-[12px] text-ink-subtle" data-id="element-3353" data-mp="1zilzomel20wh">
          Step {step + 1} of {STEPS.length}
        </span>
      </div>
    </Panel>;
}
function DocumentUpload({
  document,
  onChange
}: {
  document: ApplicationDocument;
  onChange: (next: ApplicationDocument) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const provided = document.state !== 'Not provided';
  function take(file: File | undefined) {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setProblem('That file type is not accepted. Use JPG, PNG or PDF.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setProblem(`That file is ${sizeLabel(file.size)}. The limit is 5 MB.`);
      return;
    }
    setProblem(null);
    onChange({
      ...document,
      fileName: file.name,
      sizeBytes: file.size,
      mimeType: file.type,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      state: 'Submitted',
      reviewNote: null
    });
  }
  return <div className="rounded-card border border-line bg-surface px-4 py-3.5" data-id="element-3354" data-mp="2nimhrrmuy5eo">
      <div className="flex flex-wrap items-center gap-2" data-id="element-3355" data-mp="1313menopx8sp">
        <p className="text-[13px] font-semibold text-ink" data-id="element-3356" data-mp="12zit11e53vwf">{document.label}</p>
        {document.required && <Badge tone="neutral" data-id="element-3357" data-mp="17ug7inu6uxgp">Required</Badge>}
        {provided && <Badge tone="ok" data-id="element-3358" data-mp="u206fskul9if">{document.state}</Badge>}
      </div>
      <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-3359" data-mp="yzs6j9vnc1xp">{document.hint}</p>

      {provided ? <div className="mt-2.5 flex flex-wrap items-center gap-3" data-id="element-3360" data-mp="1mpvzmnb7kjag">
          {document.previewUrl ? <img src={document.previewUrl} alt={`${document.label} preview`} className="h-16 w-24 rounded-md border border-line object-cover" data-id="element-3361" data-mp="1qrptabmzkn14" /> : <span className="flex h-16 w-24 items-center justify-center rounded-md border border-line bg-canvas" data-id="element-3362" data-mp="1hpglhtj3r7rf">
              <FileTextIcon className="h-5 w-5 text-ink-subtle" data-id="element-3363" data-mp="1bi4iuvv1e6l" />
            </span>}
          <span className="min-w-0 flex-1" data-id="element-3364" data-mp="6c5s16kvrcw2">
            <span className="block truncate text-[13px] text-ink" data-id="element-3365" data-mp="6904hvrwgj2g">{document.fileName}</span>
            <span className="cz-num block text-[12px] text-ink-subtle" data-id="element-3366" data-mp="3igguaw4rpvlb">
              {document.sizeBytes ? sizeLabel(document.sizeBytes) : ''}
            </span>
          </span>
          <Button size="sm" variant="ghost" icon={<XIcon className="h-3.5 w-3.5" data-id="element-3368" data-mp="34m6ex5envdrx" />} onClick={() => onChange({
        ...document,
        fileName: null,
        sizeBytes: null,
        mimeType: null,
        previewUrl: null,
        state: 'Not provided',
        reviewNote: null
      })} data-id="element-3367" data-mp="39khmf9hhhc6z">
            Remove
          </Button>
        </div> : <Button size="sm" variant="secondary" className="mt-2.5" icon={<UploadIcon className="h-3.5 w-3.5" data-id="element-3370" data-mp="ou0lcc92p50o" />} onClick={() => inputRef.current?.click()} data-id="element-3369" data-mp="2lqga7afcg3ac">
          Upload {document.label.toLowerCase()}
        </Button>}

      <input ref={inputRef} type="file" accept={ACCEPTED.join(',')} className="sr-only" onChange={event => take(event.target.files?.[0])} data-id="element-3371" data-mp="rg9ej1r0mkkw" />

      {problem && <p role="alert" className="mt-2 text-[12px] leading-5 text-danger-700" data-id="element-3372" data-mp="118fhf6it2mev">
          {problem}
        </p>}
      {document.reviewNote && <p className="mt-2 text-[12px] leading-5 text-warn-700" data-id="element-3373" data-mp="8igpn3d88b6i">Reviewer: {document.reviewNote}</p>}
    </div>;
}